import { EncryptedStudentStore, installationProof } from "@chams-pay/local-store";

export const SERVER_SESSION_INVALID_EVENT = "chams:server-session-invalid";
export const INSTALLATION_REVOKED_EVENT = "chams:installation-revoked";
export const DEFAULT_API_TIMEOUT_MS = 12_000;
export const SESSION_RESTORE_TIMEOUT_MS = 8_000;
let serverClockOffsetMilliseconds = 0;

export function alignedServerNow(): number {
  return Date.now() + serverClockOffsetMilliseconds;
}

function observeServerClock(response: Response, startedAt: number, receivedAt: number): void {
  const serverTime = Date.parse(response.headers.get("date") ?? "");
  if (!Number.isFinite(serverTime)) return;
  serverClockOffsetMilliseconds = serverTime - (startedAt + receivedAt) / 2;
}

function suppressSessionInvalidEvent(url: string): boolean {
  const path = new URL(url, location.origin).pathname;
  return path === "/api/session" || path === "/api/auth/device-resume";
}

export type ApiRequestFailureKind =
  "authentication" | "authorization" | "timeout" | "transport" | "server" | "response";

export class ApiRequestError extends Error {
  readonly kind: ApiRequestFailureKind;
  readonly status: number | null;
  readonly code: string | null;
  readonly correlationId: string | null;

  constructor({
    kind,
    message,
    status = null,
    code = null,
    correlationId = null,
    cause,
  }: {
    readonly kind: ApiRequestFailureKind;
    readonly message: string;
    readonly status?: number | null;
    readonly code?: string | null;
    readonly correlationId?: string | null;
    readonly cause?: unknown;
  }) {
    super(message, { cause });
    this.name = "ApiRequestError";
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

/**
 * The single encrypted replica for the whole application. Desktop and mobile
 * presentations share this instance; neither owns storage behaviour.
 */
export const localStore = new EncryptedStudentStore();

export type DeviceRequest = (url: string, init?: RequestInit) => Promise<Response>;

export function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replaceAll("-", "+")
    .replaceAll("_", "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

export function encodeBase64Url(value: Uint8Array): string {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/g, "");
}

function responseFailureKind(status: number): ApiRequestFailureKind {
  if (status === 401) return "authentication";
  if (status === 403) return "authorization";
  if (status >= 500) return "server";
  return "response";
}

async function responseError(response: Response): Promise<ApiRequestError> {
  let code: string | null = null;
  let correlationId: string | null = null;
  try {
    const body: unknown = await response.clone().json();
    if (body && typeof body === "object") {
      const candidate = body as { readonly code?: unknown; readonly correlationId?: unknown };
      if (typeof candidate.code === "string") code = candidate.code;
      if (typeof candidate.correlationId === "string") correlationId = candidate.correlationId;
    }
  } catch {
    /* A non-JSON error response still retains its status and failure kind. */
  }
  return new ApiRequestError({
    kind: responseFailureKind(response.status),
    message: `Request failed: ${response.status}`,
    status: response.status,
    code,
    correlationId,
  });
}

export function apiRequestFailureKind(reason: unknown): ApiRequestFailureKind {
  return reason instanceof ApiRequestError ? reason.kind : "response";
}

export async function apiFetch(
  url: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_API_TIMEOUT_MS,
): Promise<Response> {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const signal = init.signal ? AbortSignal.any([init.signal, timeoutSignal]) : timeoutSignal;
  const startedAt = Date.now();
  try {
    const response = await fetch(url, { ...init, signal });
    observeServerClock(response, startedAt, Date.now());
    if (response.status === 401 && !suppressSessionInvalidEvent(url))
      window.dispatchEvent(new Event(SERVER_SESSION_INVALID_EVENT));
    return response;
  } catch (reason) {
    if (init.signal?.aborted) throw reason;
    if (
      (reason instanceof DOMException && reason.name === "TimeoutError") ||
      (timeoutSignal.aborted &&
        timeoutSignal.reason instanceof DOMException &&
        timeoutSignal.reason.name === "TimeoutError")
    ) {
      throw new ApiRequestError({
        kind: "timeout",
        message: `Request timed out after ${timeoutMs} ms`,
        cause: reason,
      });
    }
    throw new ApiRequestError({
      kind: "transport",
      message: "Request could not reach the server",
      cause: reason,
    });
  }
}

export async function json<T>(response: Response): Promise<T> {
  if (!response.ok) throw await responseError(response);
  try {
    return (await response.json()) as T;
  } catch (reason) {
    throw new ApiRequestError({
      kind: "response",
      message: "Server response was not valid JSON",
      status: response.status,
      cause: reason,
    });
  }
}

export async function deviceFetch(
  url: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_API_TIMEOUT_MS,
): Promise<Response> {
  const method = init.method ?? "GET";
  const request = async () => {
    const proof = await installationProof(method, url, new Date(alignedServerNow()).toISOString());
    if (!proof) {
      throw new ApiRequestError({
        kind: "authorization",
        message: "Installation identity is unavailable",
        code: "INSTALLATION_REQUIRED",
      });
    }
    return apiFetch(url, { ...init, headers: { ...proof, ...init.headers } }, timeoutMs);
  };
  let response = await request();
  if (response.status === 403) {
    try {
      const body: unknown = await response.clone().json();
      if (
        body &&
        typeof body === "object" &&
        (body as { readonly code?: unknown }).code === "DEVICE_TIME_UNALIGNED"
      )
        response = await request();
    } catch {
      /* The ordinary error path below retains the first response. */
    }
  }
  if (response.status === 403) {
    try {
      const body: unknown = await response.clone().json();
      if (
        body &&
        typeof body === "object" &&
        (body as { readonly code?: unknown }).code === "INSTALLATION_REVOKED"
      ) {
        window.dispatchEvent(new Event(INSTALLATION_REVOKED_EVENT));
      }
    } catch {
      /* A malformed error response is handled by the caller without changing local authority. */
    }
  }
  return response;
}
